import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { EditorialPlaylistData, EditorialPlaylistTableRow } from '../../services/api/artist-analytics.service';
import {
  getCategoryAxisLayout,
  maxGridBottomPct,
  splitWordsOnSpace,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface EditorialPlaylistMixedChartProps {
  platformArtistId?: string;
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const EditorialPlaylistMixedChart: React.FC<EditorialPlaylistMixedChartProps> = ({
  platformArtistId,
  artistId,
  title = 'Editorial Playlist Reach',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<EditorialPlaylistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await artistAnalyticsService.getEditorialPlaylists(platformArtistId);
        setData(response.data);
      } catch (err) {
        setError('Failed to load editorial playlist data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, refreshKey]);

  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000000000) {
      return `${(num / 1000000000000).toFixed(1)}T`;
    } else if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)}B`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const getChartOption = (): EChartsOption => {
    if (!data || !data.table || data.table.length === 0) return {};

    // Sort table data by editorial reach descending
    const sortedTableData = [...data.table].sort((a, b) => b.rawEditorialReach - a.rawEditorialReach);
    
    const platforms = sortedTableData.map(item => item.platform);
    const editorialReachData = sortedTableData.map(item => item.rawEditorialReach);
    const playlistCountData = sortedTableData.map(item => item.rawEditorialPlaylists);
    const xLayout = getCategoryAxisLayout(platforms.length);

    // Use first platform's color for bar legend
    const firstPlatformColor = sortedTableData.length > 0 
      ? sortedTableData[0].color || '#6366f1'
      : '#6366f1';

    return {
      backgroundColor: '#ffffff',
      color: [firstPlatformColor, '#F59E0B'], // Set explicit colors for series and legend (first platform color for bars, orange for line)
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        hideDelay: 0,
        enterable: false,
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const tableItem = sortedTableData[dataIndex];
          
          return `
            <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${tableItem.platform}</div>
            <div style="color: ${tableItem.color}; margin-bottom: 4px;">
              ● Editorial Reach: ${formatLargeNumber(tableItem.rawEditorialReach)}
            </div>
            <div style="color: #F59E0B; margin-bottom: 4px;">
              ● Editorial Playlists: ${formatLargeNumber(tableItem.rawEditorialPlaylists)}
            </div>
            <div style="color: #9ca3af; font-size: 11px;">
              Avg Reach per Editorial: ${formatLargeNumber(tableItem.rawAvgReach)}
            </div>
          `;
        }
      },
      legend: {
        data: ['Editorial Reach', 'Editorial Playlists'],
        textStyle: {
          color: '#6b7280',
          fontSize: 11
        },
        top: 5,
        right: 20
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '20%',
        bottom: maxGridBottomPct('25%', xLayout.gridBottom),
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: platforms,
        axisPointer: {
          type: 'shadow'
        },
        axisLine: {
          lineStyle: {
            color: '#d1d5db'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 13,
          formatter: (value: string) => splitWordsOnSpace(value, 11)
        },
        axisTick: {
          show: false
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Editorial Reach',
          position: 'left',
          alignTicks: true,
          nameTextStyle: {
            color: '#6b7280',
            fontSize: 10
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 10,
            formatter: function(value: number) {
              return formatLargeNumber(value);
            }
          },
          splitLine: {
            lineStyle: {
              color: '#d1d5db',
              width: 1
            }
          }
        },
        {
          type: 'value',
          name: 'Playlists',
          position: 'right',
          alignTicks: true,
          nameTextStyle: {
            color: '#F59E0B',
            fontSize: 10
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            color: '#F59E0B',
            fontSize: 10,
            formatter: function(value: number) {
              return value.toString();
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: 'Editorial Reach',
          type: 'bar',
          yAxisIndex: 0,
          color: firstPlatformColor, // Set color at series level for legend (uses first platform's color)
          data: sortedTableData.map((item) => ({
            value: item.rawEditorialReach,
            itemStyle: {
              color: item.color,
              borderRadius: [4, 4, 0, 0]
            }
          })),
          barWidth: '60%',
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          animationDelay: function (idx: number) {
            return idx * 100;
          }
        },
        {
          name: 'Editorial Playlists',
          type: 'line',
          yAxisIndex: 1,
          color: '#F59E0B', // Set color at series level for legend
          data: playlistCountData,
          lineStyle: {
            color: '#F59E0B',
            width: 3
          },
          itemStyle: {
            color: '#F59E0B',
            borderWidth: 2,
            borderColor: '#1C1C1C'
          },
          symbol: 'circle',
          symbolSize: 8,
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(245, 158, 11, 0.5)'
            }
          },
          animationDelay: function (idx: number) {
            return idx * 100 + 500;
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  // Check if data is empty (no table data or all values are zero)
  const hasNoData = !data.table || 
                    data.table.length === 0 || 
                    (data.summary && data.summary.totalEditorialPlaylists === 0) ||
                    data.table.every(row => (row.rawEditorialPlaylists || 0) === 0);

  if (hasNoData) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-sm">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent px-2 pt-2 pb-1">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default EditorialPlaylistMixedChart;
